import { useCallback } from 'react'
import { getDatabase, ref, get, set, update, remove } from 'firebase/database'
import { uid } from 'uid'
import { auth } from '../auth/firebase'
import { ODUL_ONERI_RTDB_ROOT } from '../helper/odulOneriDepartments'
import { toastErrorNotify, toastSuccessNotify } from '../helper/ToastNotify'
import {
  normalizeMemberFacilities,
  resolveDepartmentFacility,
  DEFAULT_DEPARTMENT_FACILITY,
} from '../helper/odulOneriFacilities'
import {
  ACCESS_CONTROL_ROOT,
  emailToKey,
  emptyReports,
} from '../helper/accessControlConstants'
import {
  getPermissionsCache,
  setPermissionsCache,
} from '../helper/authorizedUsers'

const ROOT = ODUL_ONERI_RTDB_ROOT
const ACCESS_ROOT = ACCESS_CONTROL_ROOT

const mapDepartments = (snapshotVal) => {
  if (!snapshotVal) return []
  return Object.entries(snapshotVal)
    .map(([id, dept]) => ({
      id,
      name: dept?.name || id,
      facility: resolveDepartmentFacility(dept?.facility),
      managerName: dept?.managerName || '',
      managerEmail: dept?.managerEmail || '',
      active: dept?.active !== false,
    }))
    .sort((a, b) => {
      const facilityCmp = a.facility.localeCompare(b.facility, 'tr')
      if (facilityCmp !== 0) return facilityCmp
      return a.name.localeCompare(b.name, 'tr')
    })
}

const mapBoardMembers = (snapshotVal) => {
  if (!snapshotVal) return []
  return Object.entries(snapshotVal)
    .map(([id, member]) => ({
      id,
      fullName: member?.fullName || id,
      title: member?.title || '',
      email: member?.email || '',
      role: member?.role === 'baskan' ? 'baskan' : 'uye',
      facilities: normalizeMemberFacilities(member?.facilities),
      active: member?.active !== false,
    }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'))
}

const mapOpexUsers = (snapshotVal) => {
  if (!snapshotVal) return []
  return Object.entries(snapshotVal)
    .map(([id, user]) => ({
      id,
      fullName: user?.fullName || id,
      title: user?.title || '',
      email: user?.email || '',
      active: user?.active !== false,
    }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'))
}

/** access-control/permissions içinde odul_opex bayrağını aç/kapat (diğer yetkiler korunur) */
const syncOdulOpexPermission = async (db, email, enabled) => {
  const normalized = String(email || '').trim().toLowerCase()
  if (!normalized.includes('@')) return
  const key = emailToKey(normalized)
  const snap = await get(ref(db, `${ACCESS_ROOT}/permissions/${key}`))
  const existing = snap.val() || {}
  const reports = {
    ...emptyReports(),
    ...(existing.reports || {}),
    odul_opex: Boolean(enabled),
  }
  const payload = {
    email: normalized,
    reports,
    updatedAt: new Date().toISOString(),
    updatedBy: 'ik-config-opex',
  }
  await set(ref(db, `${ACCESS_ROOT}/permissions/${key}`), payload)
  setPermissionsCache(
    {
      ...getPermissionsCache(),
      [normalized]: payload,
    },
    true
  )
}

/** Faz 6: odul-sureci/config CRUD (departments + boardMembers + opexUsers) */
const useOdulOneriConfig = () => {
  const getDb = () => getDatabase(auth.app)

  const getDepartments = useCallback(async () => {
    try {
      const db = getDb()
      const snapshot = await get(ref(db, `${ROOT}/config/departments`))
      return mapDepartments(snapshot.val())
    } catch (error) {
      console.log('getDepartments:', error)
      toastErrorNotify('Bölüm listesi yüklenemedi.')
      return []
    }
  }, [])

  const addDepartment = useCallback(async (payload) => {
    const name = String(payload?.name || '').trim()
    const managerName = String(payload?.managerName || '').trim()
    const managerEmail = String(payload?.managerEmail || '').trim().toLowerCase()
    const facility = resolveDepartmentFacility(
      payload?.facility || DEFAULT_DEPARTMENT_FACILITY
    )
    const preferredId = String(payload?.id || '').trim()

    if (!name) {
      toastErrorNotify('Birim adı zorunludur.')
      throw new Error('Birim adı zorunludur.')
    }
    if (!managerEmail.includes('@')) {
      toastErrorNotify('Yönetici e-posta adresi zorunludur.')
      throw new Error('Yönetici e-posta adresi zorunludur.')
    }

    try {
      const db = getDb()
      const existingSnap = await get(ref(db, `${ROOT}/config/departments`))
      const existing = existingSnap.val() || {}
      const nameTaken = Object.values(existing).some((dept) => {
        const sameName =
          String(dept?.name || '')
            .trim()
            .toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr')
        const sameFacility = resolveDepartmentFacility(dept?.facility) === facility
        return sameName && sameFacility
      })
      if (nameTaken) {
        toastErrorNotify(`Bu birim ${facility} için zaten tanımlı.`)
        throw new Error(`Bu birim ${facility} için zaten tanımlı.`)
      }

      // Tercih edilen id varsa kullan; çakışırsa yeni uid üret
      let id = preferredId || uid()
      if (existing[id]) {
        id = uid()
      }

      const record = {
        name,
        facility,
        managerName,
        managerEmail,
        active: payload?.active !== false,
      }
      await set(ref(db, `${ROOT}/config/departments/${id}`), record)
      toastSuccessNotify('Birim / yönetici eklendi.')
      return { id, ...record }
    } catch (error) {
      console.log('addDepartment:', error)
      toastErrorNotify(error.message || 'Birim eklenemedi.')
      throw error
    }
  }, [])

  const updateDepartment = useCallback(async (id, payload) => {
    if (!id) throw new Error('deptId zorunludur.')

    const name = String(payload?.name || '').trim()
    const managerEmail = String(payload?.managerEmail || '').trim().toLowerCase()
    const facility = resolveDepartmentFacility(
      payload?.facility || DEFAULT_DEPARTMENT_FACILITY
    )
    if (!name) {
      toastErrorNotify('Birim adı zorunludur.')
      throw new Error('Birim adı zorunludur.')
    }
    if (!managerEmail.includes('@')) {
      toastErrorNotify('Yönetici e-posta adresi zorunludur.')
      throw new Error('Yönetici e-posta adresi zorunludur.')
    }

    try {
      const db = getDb()
      const existingSnap = await get(ref(db, `${ROOT}/config/departments`))
      const existing = existingSnap.val() || {}
      const nameTaken = Object.entries(existing).some(([deptId, dept]) => {
        if (deptId === id) return false
        const sameName =
          String(dept?.name || '')
            .trim()
            .toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr')
        const sameFacility = resolveDepartmentFacility(dept?.facility) === facility
        return sameName && sameFacility
      })
      if (nameTaken) {
        toastErrorNotify(`Bu birim ${facility} için zaten tanımlı.`)
        throw new Error(`Bu birim ${facility} için zaten tanımlı.`)
      }

      const updates = {
        name,
        facility,
        managerName: String(payload?.managerName || '').trim(),
        managerEmail,
        active: payload?.active !== false,
      }
      await update(ref(db, `${ROOT}/config/departments/${id}`), updates)
      toastSuccessNotify('Birim / yönetici güncellendi.')
      return { id, ...updates }
    } catch (error) {
      console.log('updateDepartment:', error)
      toastErrorNotify(error.message || 'Birim güncellenemedi.')
      throw error
    }
  }, [])

  const deleteDepartment = useCallback(async (id) => {
    if (!id) throw new Error('deptId zorunludur.')
    try {
      const db = getDb()
      await remove(ref(db, `${ROOT}/config/departments/${id}`))
      toastSuccessNotify('Birim silindi.')
      return true
    } catch (error) {
      console.log('deleteDepartment:', error)
      toastErrorNotify(error.message || 'Birim silinemedi.')
      throw error
    }
  }, [])

  const getBoardMembers = useCallback(async () => {
    try {
      const db = getDb()
      const snapshot = await get(ref(db, `${ROOT}/config/boardMembers`))
      return mapBoardMembers(snapshot.val())
    } catch (error) {
      console.log('getBoardMembers:', error)
      toastErrorNotify('Kurul üyeleri yüklenemedi.')
      return []
    }
  }, [])

  const addBoardMember = useCallback(async (payload) => {
    const fullName = String(payload?.fullName || '').trim()
    const email = String(payload?.email || '').trim().toLowerCase()
    const title = String(payload?.title || '').trim()
    const role = payload?.role === 'baskan' ? 'baskan' : 'uye'
    const facilities = normalizeMemberFacilities(payload?.facilities)

    if (!fullName) {
      toastErrorNotify('Ad Soyad zorunludur.')
      throw new Error('Ad Soyad zorunludur.')
    }
    if (!email.includes('@')) {
      toastErrorNotify('Kurul üyesi e-posta adresi zorunludur.')
      throw new Error('Kurul üyesi e-posta adresi zorunludur.')
    }

    try {
      const db = getDb()
      const id = uid()
      const record = {
        fullName,
        title,
        email,
        role,
        facilities,
        active: payload?.active !== false,
      }
      await set(ref(db, `${ROOT}/config/boardMembers/${id}`), record)
      toastSuccessNotify('Kurul üyesi eklendi.')
      return { id, ...record }
    } catch (error) {
      console.log('addBoardMember:', error)
      toastErrorNotify(error.message || 'Kurul üyesi eklenemedi.')
      throw error
    }
  }, [])

  const updateBoardMember = useCallback(async (id, payload) => {
    if (!id) throw new Error('memberId zorunludur.')

    const fullName = String(payload?.fullName || '').trim()
    const email = String(payload?.email || '').trim().toLowerCase()
    if (!fullName) {
      toastErrorNotify('Ad Soyad zorunludur.')
      throw new Error('Ad Soyad zorunludur.')
    }
    if (!email.includes('@')) {
      toastErrorNotify('Kurul üyesi e-posta adresi zorunludur.')
      throw new Error('Kurul üyesi e-posta adresi zorunludur.')
    }

    try {
      const db = getDb()
      const updates = {
        fullName,
        title: String(payload?.title || '').trim(),
        email,
        role: payload?.role === 'baskan' ? 'baskan' : 'uye',
        facilities: normalizeMemberFacilities(payload?.facilities),
        active: payload?.active !== false,
      }
      await update(ref(db, `${ROOT}/config/boardMembers/${id}`), updates)
      toastSuccessNotify('Kurul üyesi güncellendi.')
      return { id, ...updates }
    } catch (error) {
      console.log('updateBoardMember:', error)
      toastErrorNotify(error.message || 'Kurul üyesi güncellenemedi.')
      throw error
    }
  }, [])

  const deleteBoardMember = useCallback(async (id) => {
    if (!id) throw new Error('memberId zorunludur.')
    try {
      const db = getDb()
      await remove(ref(db, `${ROOT}/config/boardMembers/${id}`))
      toastSuccessNotify('Kurul üyesi silindi.')
      return true
    } catch (error) {
      console.log('deleteBoardMember:', error)
      toastErrorNotify(error.message || 'Kurul üyesi silinemedi.')
      throw error
    }
  }, [])

  const getOpexUsers = useCallback(async () => {
    try {
      const db = getDb()
      const snapshot = await get(ref(db, `${ROOT}/config/opexUsers`))
      return mapOpexUsers(snapshot.val())
    } catch (error) {
      console.log('getOpexUsers:', error)
      toastErrorNotify('OPEX kullanıcıları yüklenemedi.')
      return []
    }
  }, [])

  const addOpexUser = useCallback(async (payload) => {
    const fullName = String(payload?.fullName || '').trim()
    const email = String(payload?.email || '').trim().toLowerCase()
    const title = String(payload?.title || '').trim()
    const active = payload?.active !== false

    if (!fullName) {
      toastErrorNotify('Ad Soyad zorunludur.')
      throw new Error('Ad Soyad zorunludur.')
    }
    if (!email.includes('@')) {
      toastErrorNotify('OPEX kullanıcısı e-posta adresi zorunludur.')
      throw new Error('OPEX kullanıcısı e-posta adresi zorunludur.')
    }

    try {
      const db = getDb()
      const existingSnap = await get(ref(db, `${ROOT}/config/opexUsers`))
      const existing = existingSnap.val() || {}
      const emailTaken = Object.values(existing).some(
        (user) => String(user?.email || '').trim().toLowerCase() === email
      )
      if (emailTaken) {
        toastErrorNotify('Bu e-posta zaten OPEX listesinde tanımlı.')
        throw new Error('Bu e-posta zaten OPEX listesinde tanımlı.')
      }

      const id = uid()
      const record = {
        fullName,
        title,
        email,
        active,
      }
      await set(ref(db, `${ROOT}/config/opexUsers/${id}`), record)
      await syncOdulOpexPermission(db, email, active)
      toastSuccessNotify('OPEX kullanıcısı eklendi.')
      return { id, ...record }
    } catch (error) {
      console.log('addOpexUser:', error)
      toastErrorNotify(error.message || 'OPEX kullanıcısı eklenemedi.')
      throw error
    }
  }, [])

  const updateOpexUser = useCallback(async (id, payload) => {
    if (!id) throw new Error('opexUserId zorunludur.')

    const fullName = String(payload?.fullName || '').trim()
    const email = String(payload?.email || '').trim().toLowerCase()
    const active = payload?.active !== false
    if (!fullName) {
      toastErrorNotify('Ad Soyad zorunludur.')
      throw new Error('Ad Soyad zorunludur.')
    }
    if (!email.includes('@')) {
      toastErrorNotify('OPEX kullanıcısı e-posta adresi zorunludur.')
      throw new Error('OPEX kullanıcısı e-posta adresi zorunludur.')
    }

    try {
      const db = getDb()
      const prevSnap = await get(ref(db, `${ROOT}/config/opexUsers/${id}`))
      const previousEmail = String(prevSnap.val()?.email || '')
        .trim()
        .toLowerCase()

      const existingSnap = await get(ref(db, `${ROOT}/config/opexUsers`))
      const existing = existingSnap.val() || {}
      const emailTaken = Object.entries(existing).some(([userId, user]) => {
        if (userId === id) return false
        return String(user?.email || '').trim().toLowerCase() === email
      })
      if (emailTaken) {
        toastErrorNotify('Bu e-posta zaten OPEX listesinde tanımlı.')
        throw new Error('Bu e-posta zaten OPEX listesinde tanımlı.')
      }

      const updates = {
        fullName,
        title: String(payload?.title || '').trim(),
        email,
        active,
      }
      await update(ref(db, `${ROOT}/config/opexUsers/${id}`), updates)

      if (previousEmail && previousEmail !== email) {
        await syncOdulOpexPermission(db, previousEmail, false)
      }
      await syncOdulOpexPermission(db, email, active)

      toastSuccessNotify('OPEX kullanıcısı güncellendi.')
      return { id, ...updates }
    } catch (error) {
      console.log('updateOpexUser:', error)
      toastErrorNotify(error.message || 'OPEX kullanıcısı güncellenemedi.')
      throw error
    }
  }, [])

  const deleteOpexUser = useCallback(async (id) => {
    if (!id) throw new Error('opexUserId zorunludur.')
    try {
      const db = getDb()
      const snap = await get(ref(db, `${ROOT}/config/opexUsers/${id}`))
      const email = String(snap.val()?.email || '')
        .trim()
        .toLowerCase()
      await remove(ref(db, `${ROOT}/config/opexUsers/${id}`))
      if (email) {
        await syncOdulOpexPermission(db, email, false)
      }
      toastSuccessNotify('OPEX kullanıcısı silindi.')
      return true
    } catch (error) {
      console.log('deleteOpexUser:', error)
      toastErrorNotify(error.message || 'OPEX kullanıcısı silinemedi.')
      throw error
    }
  }, [])

  return {
    getDepartments,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    getBoardMembers,
    addBoardMember,
    updateBoardMember,
    deleteBoardMember,
    getOpexUsers,
    addOpexUser,
    updateOpexUser,
    deleteOpexUser,
  }
}

export default useOdulOneriConfig
